import { type FC, type ReactNode, useEffect } from 'react';

import { toast } from 'react-toastify/unstyled';

import { useAppSelector } from '@app/store/store';
import { useGetCurrentTenantInfoQuery } from '@features/tenants/api/tenants.api';
import { Badge } from '@shared/components/ui/badge';
import { textTransformRole } from '@shared/components/ui/custom/user-roles/user-roles.utils';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@shared/components/ui/sheet';
import { Skeleton } from '@shared/components/ui/skeleton';

interface ProfileDetailsPanelProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

interface InfoRowProps {
  label: string;
  value?: string | ReactNode;
  testId?: string;
  className?: string;
  isLoading?: boolean;
}

const InfoRow: FC<InfoRowProps> = ({ label, value, testId, className = '', isLoading = false }) => (
  <>
    <div className="col-span-6 font-semibold text-foreground">{label}</div>
    <div className={`col-span-6 text-muted-foreground ${className}`} data-testid={testId}>
      {isLoading ? <Skeleton className="h-4 w-full rounded-sm" /> : (value ?? '--')}
    </div>
  </>
);

export const AccountDetailsPanel: FC<ProfileDetailsPanelProps> = ({ isOpen, onOpenChange }) => {
  const { user } = useAppSelector((state) => state.authentication);
  const { data: tenantInfo, isLoading, isError } = useGetCurrentTenantInfoQuery();
  const { firstName, lastName, email, roles = [] } = user ?? {};
  const { tenantName, tenantId, companyName } = tenantInfo ?? {};

  useEffect(() => {
    if (isError) {
      toast.error('Error retrieving tenant details');
    }
  }, [isError]);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange} data-testid="account-details-sheet-root">
      <SheetContent
        side="right"
        className="bg-sidebar p-0 w-[520px] sm:max-w-[520px] gap-0"
        data-testid="account-details-sheet-content"
      >
        <SheetHeader className="border-b p-0 gap-0" data-testid="account-details-sheet-header">
          <div className="flex items-center justify-between shrink-0 h-16 px-6">
            <SheetTitle className="text-sidebar-foreground" data-testid="account-details-sheet-title">
              Account Details
            </SheetTitle>
            <SheetDescription className="sr-only" data-testid="account-details-sheet-description">
              View your account details, including personal information and tenant details
            </SheetDescription>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto scroll-container p-6">
          <div className="flex flex-col space-y-6 w-full text-sm">
            <div className="grid grid-cols-12 gap-x-4 gap-y-4">
              <InfoRow label="First Name" value={firstName} testId="account-details-drawer-field-first-name" />

              <InfoRow label="Last Name" value={lastName} testId="account-details-drawer-field-last-name" />

              <InfoRow label="Email" value={email} testId="account-details-drawer-field-email" className="break-all" />

              <InfoRow
                label="Tenant ID"
                value={tenantId}
                isLoading={isLoading}
                testId="account-details-drawer-field-tenant-id"
              />
              <InfoRow
                label="Tenant Name"
                value={tenantName}
                isLoading={isLoading}
                testId="account-details-drawer-field-tenant-name"
              />
              <InfoRow
                label="Company Name"
                value={companyName}
                isLoading={isLoading}
                testId="account-details-drawer-field-company-name"
              />

              <InfoRow
                label="Roles"
                testId="account-details-drawer-field-roles"
                value={
                  <div className="flex flex-wrap gap-2">
                    {roles.map((role: string) => (
                      <Badge key={role} variant="secondary" data-testid={`account-details-drawer-role-tag-${role}`}>
                        {textTransformRole(role)}
                      </Badge>
                    ))}
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
